/* Keep the brand size scale consistent for translated, merchant-authored and
   asynchronously inserted text. Font families, weights and casing are retained. */
(() => {
  const excluded = '.shopify-section:has(.iurop-header), .iurop-header, .iurop-drawer, .iurop-search, .lg-market, .announcement-bar, svg, script, style, noscript, iframe, template, [aria-hidden="true"]';
  const headings = 'h1,h2,h3,h4,h5,h6,[role="heading"],.iurop-returns__title,.iurop-home-showroom__title,.laganini-ugc__title,.laganini-rel__title,.iurop-contact__title,.cart-drawer__heading,.laganini-collections__title,.iurop-hero__heading,.iurop-hero__product-title,.iurop-faq__title,.laganini-drawer__title,.laganini-catgrid__label,.iurop-password__title,.laganini-404__title,.laganini-categories__label,.iurop-about__heading,.laganini-plp__title,.laganini-plp__family-name,.iurop-stores__showroom-title,.iurop-stores__feature-title,.iurop-stores__store-title,.laganini-lookbook__heading,.laganini-section-head__title,.laganini-cookies__title,.laganini-cookies-prefs__title,.footer__newsletter-heading,.footer__column-title,.iurop-featured-stockists__name';
  const buttons = 'button,[role="button"],input[type="submit"],input[type="button"],.iurop-hero__button,.laganini-lookbook__button,.laganini-wishlist__empty-cta,.cart-drawer__empty-cta,.laganini-404__btn';
  const textBlocks = 'h1,h2,h3,h4,h5,h6,p,a,button,label,summary,li,dt,dd,th,td,figcaption';
  const pending = new Set();
  let scheduled = false;

  function hasText(element) {
    return element.matches('input:not([type="hidden"]),textarea,select') ||
      (element.matches(textBlocks) && /[\p{L}\p{N}]/u.test(element.textContent)) ||
      Array.from(element.childNodes).some(node => node.nodeType === Node.TEXT_NODE && /[\p{L}\p{N}]/u.test(node.textContent));
  }

  function category(element) {
    const style = getComputedStyle(element);
    const font = style.fontFamily.toLowerCase();
    if (font.includes('agatha')) {
      if (element.closest(headings)) return 'agatha-title';
      if (element.closest(buttons)) return 'agatha-button';
      // Editorial handles are text links, not calls to action.
      if (element.closest('a') && !element.closest('.laganini-girls__handle')) return 'agatha-button';
      return 'agatha-text';
    }
    if (!/hanken|hk grotesk|hkgrotesk/.test(font)) return null;
    const block = element.closest(textBlocks) || element;
    const text = element.matches('input,textarea,select')
      ? (element.placeholder || element.value || element.textContent)
      : block.textContent;
    const hasCase = text.toLocaleUpperCase() !== text.toLocaleLowerCase();
    // Rich text de asistencia: el texto tecleado en mayusculas no cambia de tamaño
    if (style.textTransform !== 'uppercase' && element.closest('.laganini-assistance__content')) return 'hk-text';
    const uppercase = style.textTransform === 'uppercase' ||
      (style.textTransform !== 'lowercase' && style.textTransform !== 'capitalize' && hasCase && text === text.toLocaleUpperCase());
    return uppercase ? 'hk-uppercase' : 'hk-text';
  }

  function collect(root, changes) {
    if (!(root instanceof Element) || !root.isConnected || root.closest(excluded)) return;
    const visit = element => {
      if (!hasText(element)) return;
      const role = category(element);
      if (element.getAttribute('data-iurop-type') !== role) changes.push([element, role]);
    };
    visit(root);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
      acceptNode: element => element.matches(excluded) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    while (walker.nextNode()) visit(walker.currentNode);
  }

  function flush() {
    scheduled = false;
    const roots = [...pending];
    pending.clear();
    const changes = [];
    roots.filter(root => !roots.some(other => other !== root && other.contains(root)))
      .forEach(root => collect(root, changes));
    // Read computed styles first, then write attributes once, avoiding reflow per element.
    changes.forEach(([element, role]) => {
      if (role) element.setAttribute('data-iurop-type', role);
      else element.removeAttribute('data-iurop-type');
    });
    // Rich text can carry an inline !important size, which outranks stylesheets.
    roots.forEach(root => {
      if (!(root instanceof Element) || root.closest(excluded)) return;
      const inline = [root, ...root.querySelectorAll('[style]')];
      inline.forEach(element => {
        const role = element.getAttribute('data-iurop-type');
        const value = `var(--iurop-size-${role})`;
        if (role && !element.closest(excluded) && element.style.getPropertyPriority('font-size') === 'important' && element.style.fontSize !== value) {
          element.style.setProperty('font-size', value, 'important');
        }
      });
    });
  }

  function queue(root) {
    if (!(root instanceof Element) || root.closest(excluded)) return;
    pending.add(root);
    if (!scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }

  function start() {
    pending.add(document.body);
    flush();
    new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'childList' || record.type === 'characterData') {
          const parent = record.target instanceof Element ? record.target : record.target.parentElement;
          queue(parent?.closest(textBlocks) || parent);
        } else {
          queue(record.target);
        }
      }
    }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'placeholder', 'value'] });
    document.addEventListener('shopify:section:load', event => queue(event.target));
    document.addEventListener('load', event => {
      if (event.target.matches?.('link[rel="stylesheet"]')) queue(document.body);
    }, true);
    matchMedia('(max-width: 749px)').addEventListener('change', () => queue(document.body));
    window.addEventListener('load', () => queue(document.body), { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
